import IOrder_Item_PickListDTO from "../models/DTO/IOrder_Item_PickListDTO";

/** What the quantity helpers need from the supplier. */
export interface IPickListUomSupplier {
    has_order_bonus?: boolean | null;
}

/** A pick list line's quantity and the unit it is counted in, for the Simple Pick List UoM column. */
export interface IPickItemQty {
    qty: number;
    uom: string;
}

/**
 * Mirrors getPickItemQty in @orderwerks/documents (orderwerks-shared: packages/documents/src/pick-list/groupItems.ts),
 * which renders the emailed pick list PDF. Change both or the screen and the PDF will differ.
 */

/** Quantity to pick in the item's base unit: the total across the selected orders, plus frozen bonus for suppliers with order bonus. */
export const getPickItemTotalQty = (supplier: IPickListUomSupplier, item: IOrder_Item_PickListDTO): number => {
    let totalQty = Number(item.total_qty) || 0;

    if (supplier.has_order_bonus) {
        totalQty += Number(item.total_bonus_qty_frozen) || 0;
    }

    return totalQty;
};

/**
 * The unit a line is counted in when no UoM set unit applies: the base unit of the item's UoM set (order
 * quantities are stored in it), else the item's own unit of measure, else the supplier's custom unit.
 */
const getCatalogItemUom = (item: IOrder_Item_PickListDTO): string => {
    const catalogItem = item.Catalog_Item;

    return (
        catalogItem?.Base_Unit?.unit_abbr ||
        catalogItem?.Unit_Of_Measurement?.abbreviation ||
        catalogItem?.Unit_Of_Measurement_Supplier?.abbreviation ||
        ""
    );
};

/**
 * Whether the line prints in the unit it was ordered in (a UoM set unit such as a case) rather than the base
 * unit. Frozen bonus quantity is granted in base units, so a line that carries bonus falls back to base units
 * instead of silently dropping the bonus from a case count.
 */
const printsInSelectedUnit = (supplier: IPickListUomSupplier, item: IOrder_Item_PickListDTO): boolean =>
    !!item.uom_set_id &&
    !!item.Selected_Unit?.unit_abbr &&
    item.total_uom_set_qty !== null &&
    item.total_uom_set_qty !== undefined &&
    !(supplier.has_order_bonus && Number(item.total_bonus_qty_frozen) > 0);

/**
 * Quantity to pick with its unit of measure: the UoM set unit the lines were ordered in when the API grouped by
 * it ("3 CS"), otherwise the base-unit total with the catalog item's unit ("24 EA", "4 LB"). The unit is ""
 * when the item has none.
 */
export const getPickItemQty = (supplier: IPickListUomSupplier, item: IOrder_Item_PickListDTO): IPickItemQty => {
    if (printsInSelectedUnit(supplier, item)) {
        return { qty: Number(item.total_uom_set_qty) || 0, uom: item.Selected_Unit!.unit_abbr! };
    }

    return { qty: getPickItemTotalQty(supplier, item), uom: getCatalogItemUom(item) };
};
